import type { Point2D } from '@shared/models/geometry.model';

/** Default end-tick length as a fraction of the measure span (clamped). */
const TICK_LENGTH_FRACTION = 0.08;
const TICK_LENGTH_MIN_METERS = 0.15;
const TICK_LENGTH_MAX_METERS = 1.25;

export type CalibrateMeasureSegmentRole = 'calibrate-line' | 'calibrate-tick';

export type CalibrateMeasureSegment = {
  role: CalibrateMeasureSegmentRole;
  start: Point2D;
  end: Point2D;
};

/**
 * Build a blueprint-style dimension: main span plus short perpendicular ticks
 * at each end. No point markers — cursor alone indicates placement.
 */
export const buildCalibrateMeasureSegments = (
  start: Point2D,
  end: Point2D,
): CalibrateMeasureSegment[] => {
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  const length = Math.hypot(dx, dy);
  if (!(length > 0)) return [];

  const ux = dx / length;
  const uy = dy / length;
  // Unit normal for end ticks.
  const nx = -uy;
  const ny = ux;
  const tickHalf = resolveTickHalfLengthMeters(length);

  const tickAt = (point: Point2D): CalibrateMeasureSegment => ({
    role: 'calibrate-tick',
    start: [point[0] - nx * tickHalf, point[1] - ny * tickHalf],
    end: [point[0] + nx * tickHalf, point[1] + ny * tickHalf],
  });

  return [{ role: 'calibrate-line', start, end }, tickAt(start), tickAt(end)];
};

/**
 * Tick size scales gently with span so short and long measures both read.
 */
const resolveTickHalfLengthMeters = (spanMeters: number): number => {
  const full = Math.min(
    TICK_LENGTH_MAX_METERS,
    Math.max(TICK_LENGTH_MIN_METERS, spanMeters * TICK_LENGTH_FRACTION),
  );
  return full / 2;
};
